import { AlertTriangle, CreditCard, Clock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { format, formatDistanceToNow } from "date-fns";

export function PaymentWarningBanner() {
  const { subscription } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);

  // Only show for past_due subscriptions with active grace period
  if (!subscription || subscription.status !== 'past_due' || !subscription.grace_period_ends_at) {
    return null;
  }

  const gracePeriodEnd = new Date(subscription.grace_period_ends_at);
  const now = new Date();
  
  // Don't show if grace period has already expired
  if (gracePeriodEnd <= now) {
    return null;
  }

  const hoursRemaining = Math.max(0, (gracePeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60));
  const daysRemaining = Math.ceil(hoursRemaining / 24);
  const isUrgent = daysRemaining <= 1;

  const handleUpdatePayment = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`mb-6 rounded-xl border p-5 shadow-fluent-2 ${isUrgent ? 'border-destructive/40 bg-destructive/5' : 'border-amber-500/40 bg-amber-500/5'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isUrgent ? 'bg-destructive/10 text-destructive' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1 space-y-3">
          <div>
            <h3 className={`font-display font-semibold ${isUrgent ? 'text-destructive' : 'text-amber-700 dark:text-amber-300'}`}>
              {isUrgent ? 'Payment required today' : 'Payment issue detected'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Your last payment could not be processed. Please update your payment method by{' '}
              <strong className="text-foreground">{format(gracePeriodEnd, 'MMMM d, yyyy')}</strong>{' '}
              ({formatDistanceToNow(gracePeriodEnd, { addSuffix: true })}) to avoid losing your{' '}
              <strong className="text-foreground">{subscription.credits_remaining}</strong> remaining credits.
            </p>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <Button onClick={handleUpdatePayment} disabled={isLoading} variant={isUrgent ? "destructive" : "default"}>
              <CreditCard className="h-4 w-4 mr-2" />
              {isLoading ? 'Opening…' : 'Update payment method'}
            </Button>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
